
// File: components/renderer/drawScene.ts
import { COLORS } from "../config/colors";
import { CANVAS_WIDTH, CANVAS_HEIGHT } from "../config/dimensions";
import { GameInstance } from "../engine/GameInstance";
import { drawRumi, drawSwing } from "./player/drawRumi";
import { drawEnemy } from "./drawEnemy";
import { drawMira } from "./allies/drawMira";
import { drawZoey } from "./allies/drawZoey";
import { drawAllyProjectiles } from "./drawAllyProjectiles";
// FIX: Removed unused import for 'drawDerpy' which is not exported from './allies/drawDerpy'.
import { drawNamsanBackground, drawBathhouseBackground, drawCastleBackground, drawDefaultStage, drawPlaneBackground, drawPracticeBackground } from "./backgrounds";

export const drawScene = (ctx: CanvasRenderingContext2D, game: GameInstance) => {
  // Shake effect
  const ox = (Math.random() - 0.5) * game.shake;
  const oy = (Math.random() - 0.5) * game.shake;

  ctx.save();
  ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
  ctx.translate(ox, oy);

  // Background Switching based on City
  if (game.isPracticeMode) {
    drawPracticeBackground(ctx);
  } else if (game.isPlaneStage) {
    drawPlaneBackground(ctx);
  } else if (game.isBathhouseFight) {
    drawBathhouseBackground(ctx);
  } else if (game.currentCity === 'SEOUL') {
    drawNamsanBackground(ctx);
  } else if (game.currentCity === 'LONDON' || game.currentCity === 'PARIS') {
    drawCastleBackground(ctx);
  } else {
    drawDefaultStage(ctx);
  }

  // If we are only drawing the background, stop here.
  if (game.isBackgroundOnly) {
    ctx.restore(); // Balance the save() from the shake effect
    return;
  }

  // Collect all characters for depth sorting
  const entities: any[] = [
    { type: 'PLAYER', data: game.player, h: game.player.height },
    { type: 'MIRA', data: game.mira, h: game.mira.height },
    { type: 'ZOEY', data: game.zoey, h: game.zoey.height },
    ...game.enemies.map(e => ({ type: 'ENEMY', data: e, h: e.height }))
  ];

  // Sort by bottom Y (y + height)
  entities.sort((a, b) => (a.data.y + a.h) - (b.data.y + b.h));

  // Get Costume IDs
  const miraCostume = game.playerSquad.members.find(m => m.id === 'MIRA')?.costume || 'DEFAULT';
  const zoeyCostume = game.playerSquad.members.find(m => m.id === 'ZOEY')?.costume || 'DEFAULT';
  const rumiCostume = game.playerSquad.members.find(m => m.id === 'RUMI')?.costume || 'DEFAULT';
  const isPhaseTwo = game.goldenHonmoonTimer > 0;

  // Draw sorted entities
  entities.forEach(ent => {
    if (ent.type === 'MIRA') drawMira(ctx, ent.data, miraCostume, game.hideWeapon);
    if (ent.type === 'ZOEY') drawZoey(ctx, ent.data, zoeyCostume, game.hideWeapon);
    if (ent.type === 'ENEMY') drawEnemy(ctx, ent.data);
    if (ent.type === 'PLAYER') {
      drawRumi(ctx, ent.data, isPhaseTwo, rumiCostume, game.hideWeapon);
      if (ent.data.isSwinging) drawSwing(ctx, ent.data, isPhaseTwo);
    }
  });

  // Projectiles
  game.projectiles.forEach(p => {
    ctx.save();
    ctx.translate(p.x, p.y);

    // Distinct Rendering per Projectile Type
    if (p.type === 'SONIC') {
      // Concentric Rings
      ctx.strokeStyle = "#00ffff";
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(0, 0, p.radius, 0, Math.PI * 2); ctx.stroke();
      ctx.beginPath(); ctx.arc(0, 0, p.radius * 0.6, 0, Math.PI * 2); ctx.stroke();
    }
    else if (p.type === 'BEAM') {
      // Fast Beam Line
      ctx.rotate(Math.atan2(p.vy, p.vx));
      ctx.fillStyle = "#ffffff";
      ctx.shadowColor = "#facc15"; ctx.shadowBlur = 10;
      ctx.fillRect(-10, -2, 20, 4);
    }
    else if (p.type === 'HEART') {
      // Pink Heart
      ctx.fillStyle = "#ec4899";
      ctx.shadowColor = "#ec4899"; ctx.shadowBlur = 5;
      const s = p.radius / 10;
      ctx.scale(s, s);
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.bezierCurveTo(-5, -5, -10, 0, 0, 10);
      ctx.bezierCurveTo(10, 0, 5, -5, 0, 0);
      ctx.fill();
    }
    else {
      // Default Orbs
      ctx.beginPath();
      ctx.arc(0, 0, p.radius, 0, Math.PI * 2);
      ctx.fillStyle = p.type === "DARK" ? COLORS.projectileDark : (p.type === "GOLD" ? COLORS.projectileLight : COLORS.sword);
      ctx.shadowBlur = 10;
      ctx.shadowColor = ctx.fillStyle as string;
      ctx.fill();
    }

    ctx.restore();
  });

  // Ally Projectiles
  drawAllyProjectiles(ctx, game.allyProjectiles);

  // Particles
  game.particles.forEach(p => {
    ctx.fillStyle = p.color;
    ctx.globalAlpha = p.life;
    ctx.fillRect(p.x, p.y, 4, 4);
  });

  ctx.globalAlpha = 1.0;
  ctx.restore();
};
